import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Dropdown, Icon, TextInput } from '@pella/sash-framework';
import { isValidArray, isValidObject, isValidPostal } from '../../../utilities/validations';
import { usePostalCode } from '../../../../hooks/use-postal-code';
import { useShowroomUrls } from '../../../../hooks/use-showroom-urls';
import { getBranch, getMarket, getServiceInfo, getShowrooms } from '../../../../actions/location-actions';
import { savePostalCode } from '../../../utilities/functions';
import { usePDPConfig } from '../../../../hooks/use-pdp-config';

import LoadingOverlay from '../../../common/loading-overlay';
import CTA from '../../../common/cta';

const NonTransactableOptions = ({ productName, className }) => {
	const { nta_purchase_options } = usePDPConfig();
	const dispatch = useDispatch();
	const locations = useSelector((state) => state.locations);
	const userZip = usePostalCode();
	const { oldShowrooms, newShowrooms } = useShowroomUrls();
	const [zip, setZip] = useState({ input: '', saved: '' });
	const [error, setError] = useState(null);
	const [status, setStatus] = useState('idle');

	const showroomLink = newShowrooms?.nodes?.find((url) => url.showroom_id === locations?.showrooms?.[0]?.store_number)?.url || oldShowrooms?.nodes?.find((url) => url.showroom_id === locations?.showrooms?.[0]?.store_number)?.url;
	const showroomName = newShowrooms?.nodes?.find((url) => url.showroom_id === locations?.showrooms?.[0]?.store_number)?.page_heading?.replace(/<\/?span[^>]*>/g, '') || locations?.showrooms?.[0]?.name;

	useEffect(() => setZip((current) => ({ ...current, saved: userZip })), [userZip]);

	const onInputChange = (event) => setZip({ ...zip, input: event.target.value });

	const onFormSubmit = (event) => {
		event.preventDefault();
		setError(null);
		setStatus('loading');

		if (!isValidPostal(zip.input)) {
			setError('Please enter a valid ZIP code.');
			setStatus('error');
			return;
		}

		dispatch(getBranch(zip.input));
		dispatch(getShowrooms(zip.input));
		dispatch(getMarket(zip.input));
		dispatch(getServiceInfo(zip.input));

		savePostalCode(zip.input);
		setZip({ ...zip, saved: zip.input });

		document.body.click();
		setStatus('idle');
	};

	return (
		<div className={`purchaseOptions relative mx-auto max-w-80 border border-gray-10 bg-gray-5 px-4 py-6 lg:mx-0 xl:max-w-screen-sm ${className || ''}`}>
			<div className='mb-4 flex flex-row flex-wrap'>
				<p className='inline-flex flex-12/12 items-center gap-x-1 xl:flex-1'>
					{nta_purchase_options?.heading && <span className='text-tiny text-gray-60'>{nta_purchase_options.heading}</span>}
					{productName && (
						<Dropdown
							className='z-2'
							showCaret
						>
							<Dropdown.Trigger caretClass='-ml-2'>
								<Icon
									name='info'
									size={16}
								/>
							</Dropdown.Trigger>
							<Dropdown.Content className='text-body-2 left-1/2 w-64 -translate-x-1/2 md:w-80'>
								<div dangerouslySetInnerHTML={{ __html: `${productName} ${nta_purchase_options?.tooltip}` }} />
							</Dropdown.Content>
						</Dropdown>
					)}
				</p>

				<div className='flex-none text-[10px] tracking-normal text-black'>
					<span className='mr-0.5'>Based on</span>
					<Dropdown
						className='z-2 inline-block'
						showCaret
					>
						<Dropdown.Trigger
							className='underline underline-offset-2'
							caretClass='-ml-1.5'
						>
							{zip.saved}
						</Dropdown.Trigger>
						<Dropdown.Content className='-left-16 px-6 py-4 shadow-md md:-left-20 xl:-right-10 xl:left-auto'>
							{status === 'loading' && <LoadingOverlay fixed={false} />}

							<p className='mb-1 text-base font-semibold'>Update Zip Code</p>
							<p className='m-0 text-pretty text-sm'>Enter your zip code to display purchasing options.</p>

							{error && (
								<Alert
									type='error'
									className='mt-4 items-center gap-x-2 !px-2 !py-1 [&>div]:!m-0 [&>div]:text-sm [&_span]:!text-lg'
									hideClose
								>
									{error}
								</Alert>
							)}

							<form
								className='mt-4 flex w-[272px] flex-row flex-wrap items-end gap-x-1 md:w-80'
								onSubmit={onFormSubmit}
								aria-label='Update Zip Code Form'
							>
								<TextInput
									id='purchaseZip'
									onChange={onInputChange}
									value={zip.input}
									autoComplete='postal-code'
									className='[&~div]:hidden'
									groupClass='!mb-0 flex-1'
									placeholder='15206'
									labelText='Zip Code'
								/>

								<Button
									color='primary-alt'
									type='submit'
									className='min-h-[54px] !w-auto flex-none'
								>
									Submit
								</Button>
							</form>
						</Dropdown.Content>
					</Dropdown>
				</div>
			</div>

			<div className='flex flex-row flex-wrap items-stretch'>
				<div className='flex flex-1 flex-col'>
					<div className='mb-4 flex flex-row flex-wrap gap-x-2.5'>
						<Icon
							className='flex-none'
							name={nta_purchase_options?.material_symbol_free}
							size={20}
							weight={700}
						/>

						<div className='flex-1'>
							<p className='text-body-2 m-0 flex items-center gap-x-2 text-black'>
								{nta_purchase_options?.free && <span className='font-semibold'>{nta_purchase_options.free}</span>}
								<div className={`h-2 w-2 rounded-full ${isValidArray(locations?.showrooms) ? 'bg-green' : 'border-2 border-green'}`} />
							</p>

							<p className='text-caption m-0 leading-tight text-gray-60'>{isValidArray(locations?.showrooms) ? nta_purchase_options?.available : nta_purchase_options?.limited}</p>
						</div>
					</div>

					{isValidArray(nta_purchase_options?.cta) && (
						<CTA
							data={nta_purchase_options.cta[0]}
							className='xl:mt-auto'
						/>
					)}
				</div>

				<div className='my-4 h-px flex-12/12 xl:mx-4 xl:my-0 xl:flex xl:h-auto xl:w-px xl:flex-none xl:flex-col xl:justify-end'>
					<div className='h-full bg-gray-15 xl:h-[calc(100%-1.5rem)]' />
				</div>

				<div className='flex-12/12 xl:flex-1'>
					<div className='mb-4 flex flex-row flex-wrap gap-x-2'>
						<Icon
							className='flex-none'
							name={nta_purchase_options?.material_symbol_visit}
							size={20}
							weight={700}
						/>

						<div className='flex-1'>
							<p className='text-body-2 m-0 flex items-center gap-x-2 text-black'>
								{nta_purchase_options?.visit && <span className='font-semibold'>{nta_purchase_options.visit}</span>}
								<div className={`h-2 w-2 rounded-full ${isValidArray(locations?.showrooms) ? 'bg-green' : 'bg-red'}`} />
							</p>

							{!isValidArray(locations?.showrooms) && <p className='text-caption m-0 leading-tight text-gray-60'>{nta_purchase_options?.no_showroom}</p>}

							{isValidArray(locations?.showrooms) && showroomLink && showroomName && (
								<p className='text-caption m-0'>
									{' '}
									Available at <Link to={showroomLink}>{showroomName}</Link>
								</p>
							)}

							{isValidArray(locations?.showrooms) && isValidObject(locations.showrooms[0].dist) && (
								<p className='text-caption m-0 mt-0.5 text-gray-60'>
									{locations.showrooms[0].country === 'US' && locations.showrooms[0].dist.miles?.value && <span>{locations.showrooms[0].dist.miles.value} miles from you</span>}
									{locations.showrooms[0].country !== 'US' && locations.showrooms[0].dist.kilometers?.value && <span>{locations.showrooms[0].dist.kilometers.value} kilometers from you</span>}
								</p>
							)}
						</div>
					</div>

					{isValidArray(locations?.showrooms) && (
						<Button
							href={`https://maps.google.com/?q=${showroomName}`}
							rel='noopener noreferrer'
							target='_blank'
							title='Link opens in a new tab'
							color='secondary'
							className='bg-white xl:mt-auto'
							wide
						>
							Get Directions
						</Button>
					)}

					{!isValidArray(locations?.showrooms) && nta_purchase_options?.cta?.[1] && (
						<CTA
							data={nta_purchase_options.cta[1]}
							className='bg-white xl:mt-auto'
							wide
						/>
					)}
				</div>
			</div>
		</div>
	);
};

NonTransactableOptions.propTypes = {
	className: PropTypes.string,
	productName: PropTypes.string,
};

export default NonTransactableOptions;
